
import React, { useRef } from "react";
import { Link } from "react-router-dom";

import "./MyProgress.css";
import { buildProgressItems } from "../utils/dashboardStats";
import useDashboardRowDrag from "./useDashboardRowDrag";
import { DASHBOARD_ROUTES } from "./dashboardRoutes";

const PROGRESS_COLORS = [
  "#7866ed",
  "#f4bf52",
  "#f4bf52",
  "#f4bf52",
];

const ProgressCardSkeleton = () => (
  <div
    className="my-progress-card my-progress-card--skeleton"
    aria-hidden="true"
  >
    <span className="dashboard-skeleton my-progress-card__skeleton-title" />
    <span className="dashboard-skeleton my-progress-card__skeleton-count" />
    <span className="dashboard-skeleton my-progress-card__skeleton-icon" />
    <span className="dashboard-skeleton my-progress-card__skeleton-track" />
  </div>
);

const MyProgress = ({
  stats = [],
  isLoading = false,
  error = "",
}) => {
  const rowRef = useRef(null);

  useDashboardRowDrag(rowRef);

  // Build cards dynamically from the provided API stats.
  const progressItems = buildProgressItems(stats);

  return (
    <section
      className="my-progress"
      aria-labelledby="my-progress-title"
    >
      {/* Header */}
      <div className="quick-action__header">
        <h2
          id="my-progress-title"
          className="quick-action__title"
        >
          My Progress
        </h2>

        <Link
          to={DASHBOARD_ROUTES.progress}
          className="ps-2 my-progress-link"
          aria-label="View all progress"
          title="View all progress"
        >
          <i
            className="bi bi-arrow-right-circle-fill"
            aria-hidden="true"
          />
        </Link>
      </div>

      {/* Error State */}
      {!isLoading && error ? (
        <p
          role="alert"
          className="my-progress__status my-progress__status--error"
        >
          {error}
        </p>
      ) : null}

      {/* Empty State */}
      {!isLoading && !error && progressItems.length === 0 ? (
        <p
          role="status"
          className="my-progress__status"
        >
          No progress data available.
        </p>
      ) : null}

      {/* Progress Cards */}
      <div
        ref={rowRef}
        className="my-progress__grid"
        role="region"
        aria-label="My Progress cards"
        aria-busy={isLoading}
        tabIndex={0}
      >
        {/* Loading Skeletons */}
        {isLoading
          ? Array.from({ length: 4 }, (_, index) => (
              <ProgressCardSkeleton key={index} />
            ))
          : null}

        {/* Dynamic API Data */}
        {!isLoading && !error
          ? progressItems.map((item) => {
              const completed = Number(item.completed) || 0;
              const total = Number(item.total) || 0;

              const percentage =
                total > 0
                  ? Math.min(
                      100,
                      Math.max(0, (completed / total) * 100)
                    )
                  : 0;

              const toneIndex =
                Number(item.toneIndex) || 0;

              const accent =
                PROGRESS_COLORS[
                  toneIndex % PROGRESS_COLORS.length
                ];

              return (
                <article
                  key={item.id}
                  className="my-progress-card"
                  style={{
                    "--progress-accent": accent,
                  }}
                >
                  <h3
                    className="my-progress-card__title"
                    title={item.label}
                  >
                    {item.label}
                  </h3>

                  <div className="my-progress-card__middle">
                    <p className="my-progress-card__count">
                      <strong>{completed}</strong>
                      <span> / {total}</span>
                    </p>

                    <span
                      className="my-progress-card__icon"
                      aria-hidden="true"
                    >
                      {item.image ? (
                        <img src={item.image} alt="" />
                      ) : (
                        <i className="bi bi-bar-chart-fill" />
                      )}
                    </span>
                  </div>

                  <div className="my-progress-card__bottom">
                    <div
                      className="my-progress-card__track"
                      role="progressbar"
                      aria-label={`${item.label} progress`}
                      aria-valuenow={Math.round(percentage)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <span
                        className="my-progress-card__fill"
                        style={{
                          width: `${percentage}%`,
                        }}
                      />
                    </div>

                    <span className="my-progress-card__percent">
                      {Math.round(percentage)}%
                    </span>
                  </div>
                </article>
              );
            })
          : null}
      </div>
    </section>
  );
};

export default MyProgress;
